/**
 * ----------------------------------------------------------------------------------------------------------------------
 * NORELLA CUSTOM SCRIPTS
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Small custom elements used by the "nf-" sections and product blocks. Loaded as a module after theme.js.
 */

const sizeNumber = (value) => parseFloat(String(value).replace(",", ".").replace(/[^0-9.]/g, ""));

/**
 * Size localization. The store sells in one size system (e.g. US); visitors from the UK, Australia, Europe... see their
 * own system on the size buttons, in the bundle and in the size finder. Tables are "US, EU, UK, cm" rows per gender.
 *
 * AU sizes: women follow US women's numbers, men follow UK numbers.
 */
const NfSizes = (() => {
  const config = window.NorellaSizes || {};
  const parse = (text) => String(text || "").split(/\n+/).map((line) => line.split(",").map((cell) => parseFloat(cell.trim().replace(/[^0-9.]/g, "")))).filter((row) => row.length >= 4 && row.every((cell) => !isNaN(cell)));
  const tables = { women: parse(config.tables?.women), men: parse(config.tables?.men) };
  const usual = parse(config.usual); // General brand conversion: EU, UK, US Men, US Women
  const columns = { us: 0, eu: 1, uk: 2 };

  // Column of the general conversion table for a size system and gender
  const usualColumn = (targetSystem, gender) => {
    if (targetSystem === "eu") return 0;
    if (targetSystem === "uk") return 1;
    if (targetSystem === "au") return gender === "women" ? 3 : 1;
    return gender === "women" ? 3 : 2;
  };
  const codes = (text) => String(text || "").toUpperCase().split(",").map((code) => code.trim()).filter(Boolean);
  const storeSystem = config.storeSystem || "us";
  const country = String(config.country || "").toUpperCase();

  let system = storeSystem;
  if (config.localize) {
    ["us", "eu", "uk", "au"].forEach((key) => {
      if (codes(config.countries?.[key]).includes(country)) system = key;
    });
  }

  const genderOf = (values) => {
    const lower = (values || []).map((value) => String(value).toLowerCase().trim());
    if (lower.includes(String(config.women || "Women").toLowerCase())) return "women";
    if (lower.includes(String(config.men || "Men").toLowerCase())) return "men";
    return null;
  };

  const column = (targetSystem, gender) => targetSystem === "au" ? (gender === "women" ? 0 : 2) : columns[targetSystem];
  const format = (number) => String(number);

  // Converts a store size value (e.g. "US8") to the visitor's system. Returns null when no translation applies.
  const convert = (value, gender) => {
    const number = sizeNumber(value);
    const row = tables[gender || "men"]?.find((item) => item[columns[storeSystem]] === number);

    if (isNaN(number) || !row || system === storeSystem) {
      return null;
    }

    return { local: `${system.toUpperCase()} ${format(row[column(system, gender || "men")])}`, original: `${storeSystem.toUpperCase()} ${format(number)}` };
  };

  return {
    tables, usual, usualColumn, system, storeSystem, genderOf, column, convert,
    label: (value, gender) => convert(value, gender)?.local || value,
    full: (value, gender) => {
      const converted = convert(value, gender);
      return !converted ? value : config.showOriginal ? `${converted.local} (${converted.original})` : converted.local;
    },
    showOriginal: !!config.showOriginal
  };
})();

/**
 * Relabels the size buttons of the main variant picker (and the "Size: ..." text) for the visitor's size system.
 */
const localizeVariantPickerSizes = () => {
  if (NfSizes.system === NfSizes.storeSystem) {
    return;
  }

  document.querySelectorAll("variant-picker").forEach((picker) => {
    const genderInput = picker.querySelector("[data-nf-gender-option] input:checked");
    const gender = NfSizes.genderOf(genderInput ? [genderInput.value] : []) || "men";

    picker.querySelectorAll(".variant-picker__option--size").forEach((fieldset) => {
      fieldset.querySelectorAll("label[data-option-value]").forEach((label) => {
        const input = document.getElementById(label.getAttribute("for"));
        const converted = input ? NfSizes.convert(input.value, gender) : null;
        const text = label.querySelector("span:not(.nf-size-original)");

        if (!converted || !text) {
          return;
        }

        text.textContent = converted.local;

        let original = label.querySelector(".nf-size-original");
        if (NfSizes.showOriginal && !original) {
          original = document.createElement("span");
          original.className = "nf-size-original";
          label.appendChild(original);
        }
        if (original) original.textContent = converted.original;
      });

      const selected = fieldset.querySelector("input:checked");
      const selectedText = fieldset.querySelector("variant-option-value");
      if (selected && selectedText) selectedText.textContent = NfSizes.full(selected.value, gender);
    });
  });
};

document.addEventListener("variant:change", () => setTimeout(localizeVariantPickerSizes, 0));

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", localizeVariantPickerSizes);
} else {
  localizeVariantPickerSizes();
}

const formatMoney = (cents, format = window.themeVariables?.settings?.moneyFormat || "{{amount}}") => {
  const withDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    const [whole, fraction] = (number / 100).toFixed(precision).split(".");
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : "");
  };

  const formats = {
    amount: () => withDelimiters(cents, 2),
    amount_no_decimals: () => withDelimiters(cents, 0),
    amount_with_comma_separator: () => withDelimiters(cents, 2, ".", ","),
    amount_no_decimals_with_comma_separator: () => withDelimiters(cents, 0, ".", ","),
    amount_with_space_separator: () => withDelimiters(cents, 2, " ", ","),
    amount_no_decimals_with_space_separator: () => withDelimiters(cents, 0, " "),
    amount_with_apostrophe_separator: () => withDelimiters(cents, 2, "'", ".")
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
};

/**
 * Evergreen countdown. The end date is stored per visitor so the timer does not reset on every page load, and restarts
 * automatically once it reaches zero.
 */
class NfCountdown extends HTMLElement {
  connectedCallback() {
    const hours = parseFloat(this.getAttribute("hours") || "2");
    const key = `nf-countdown-${this.getAttribute("key") || "default"}`;
    let end = 0;

    try { end = parseInt(localStorage.getItem(key) || "0", 10); } catch (e) {}

    if (!end || end < Date.now()) {
      end = Date.now() + hours * 3600 * 1000;
      try { localStorage.setItem(key, String(end)); } catch (e) {}
    }

    this.end = end;
    this.hours = hours;
    this.key = key;
    this._tick();
    this.interval = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  _tick() {
    let remaining = Math.max(0, this.end - Date.now());

    if (remaining === 0) {
      this.end = Date.now() + this.hours * 3600 * 1000;
      try { localStorage.setItem(this.key, String(this.end)); } catch (e) {}
      remaining = this.end - Date.now();
    }

    const pad = (value) => String(value).padStart(2, "0");
    const h = Math.floor(remaining / 3600000), m = Math.floor(remaining % 3600000 / 60000), s = Math.floor(remaining % 60000 / 1000);
    this.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
}

/**
 * Quantity breaks (1 / 2 / 3 pairs) with a selection per pair.
 *
 * - Pair 1 is always the product being viewed, in sync with the main variant picker (both ways).
 * - Other pairs follow the main picker until the customer changes them. When "bundle products" are configured, pairs 2+
 *   can be a different shoe, picked from a carousel of model cards (like North).
 * - On add to cart, the product form data is rewritten so that each pair is added as its own line item.
 *
 * Actual discounts must be configured as automatic discounts in the Shopify admin.
 */
class NfQuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute("form")];
    this.product = JSON.parse(this.querySelector("[data-product-json]")?.textContent || "null");
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.cards = Array.from(this.querySelectorAll(".nf-breaks__option"));

    let models = [];
    try { models = JSON.parse(this.querySelector("[data-bundle-models]")?.textContent || "[]"); } catch (e) {}
    this.models = models.length ? models : (this.product ? [{ product: this.product, meta: { title: this.product.title } }] : []);
    this.modelsById = new Map(this.models.map((model) => [model.product.id, model]));
    this.swatches = this._parseSwatches(JSON.parse(this.querySelector("[data-swatch-config]")?.textContent || '""'));

    const hasVariants = this.product && !this.product.has_only_default_variant && this.product.variants.length > 1;
    this.withPairSelectors = this.hasAttribute("pair-selectors") && (hasVariants || this.models.length > 1);

    const labels = this.querySelector("template[data-labels]")?.content;
    const label = (selector, fallback) => labels?.querySelector(selector)?.textContent || fallback;
    this.labels = {
      first: label("[data-first-pair]", "Pair 1"),
      other: label("[data-other-pair]", "Pair [n]"),
      model: label("[data-model-pair]", "Pair [n] – Choose your [ordinal] pair"),
      ordinals: label("[data-ordinals]", "first, second, third").split(",").map((word) => word.trim()),
      summary: label("[data-summary]", "Pair [n]: [model] · [color]"),
      soldOut: label("[data-sold-out]", "Sold out")
    };

    const maxQuantity = Math.max(1, ...this.cards.map((card) => parseInt(card.getAttribute("data-quantity"), 10)));
    const initial = this._mainVariant()?.options || this.product?.variants[0]?.options || [];
    this.pairs = Array.from({ length: maxQuantity }, () => ({ productId: this.product?.id, options: [...initial], touched: false }));

    this._abort = new AbortController();
    const signal = this._abort.signal;

    this.addEventListener("change", this._onChange.bind(this), { signal });
    this.addEventListener("click", this._onClick.bind(this), { signal });
    this.form?.addEventListener("variant:change", this._onVariantChanged.bind(this), { signal });
    nfInstallSubmitHandler(this.form);

    this._onSelectionChanged();
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  get selectedCard() {
    return this.cards.find((card) => card.querySelector('input[type="radio"]:checked')) || this.cards[0];
  }

  get quantity() {
    return parseInt(this.selectedCard?.getAttribute("data-quantity") || "1", 10);
  }

  get mixModels() {
    return this.models.length > 1;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Events
   * ------------------------------------------------------------------------------------------------------------ */

  _onChange(event) {
    const target = event.target;

    if (target.matches('input[type="radio"]')) {
      this._onSelectionChanged();
    } else if (target.matches("select[data-pair]")) {
      this._onPairOptionChanged(parseInt(target.getAttribute("data-pair"), 10), parseInt(target.getAttribute("data-option"), 10), target.value);
    }
  }

  _onClick(event) {
    const swatch = event.target.closest("[data-model-swatch]");
    const card = event.target.closest("[data-model-card]");
    const arrow = event.target.closest("[data-models-scroll]");

    if (arrow) {
      const track = arrow.closest("[data-pair-row]").querySelector(".nf-models__track");
      track.scrollBy({ left: parseInt(arrow.getAttribute("data-models-scroll"), 10) * track.clientWidth * 0.8, behavior: "smooth" });
      return;
    }

    if (!card) {
      return;
    }

    const pairIndex = parseInt(card.closest("[data-pair-row]").getAttribute("data-pair-row"), 10);
    const productId = parseInt(card.getAttribute("data-model-card"), 10);
    this._selectModel(pairIndex, productId, swatch?.getAttribute("data-model-swatch"));
  }

  _onSelectionChanged() {
    if (this.quantityInput) {
      this.quantityInput.value = this.quantity;
    }

    this.cards.forEach((card) => {
      const container = card.querySelector("[data-pairs]");

      if (!container) {
        return;
      }

      const isSelected = card === this.selectedCard;
      container.hidden = !isSelected || !this.withPairSelectors;
      container.innerHTML = "";

      if (isSelected && this.withPairSelectors) {
        this._buildRows(container, parseInt(card.getAttribute("data-quantity"), 10));
      }
    });

    this._update();
  }

  _onVariantChanged(event) {
    const variant = event.detail.variant;

    if (variant) {
      this.pairs.forEach((pair, index) => {
        if (index === 0 || (!pair.touched && pair.productId === this.product.id)) {
          pair.productId = this.product.id;
          pair.options = [...variant.options];
        }
      });
    }

    // The theme re-renders the add to cart button text on variant change, so we update after it
    setTimeout(() => this._update(), 0);
  }

  _onPairOptionChanged(pairIndex, optionIndex, value) {
    const pair = this.pairs[pairIndex];
    const product = this._productOf(pair);
    pair.options[optionIndex] = value;

    // If the combination does not exist or is sold out, keep the changed option and pick the closest available match
    if (!this._isAvailable(pair.options, product)) {
      const distance = (variant) => variant.options.reduce((sum, option, index) => {
        if (index === optionIndex) return sum;
        const a = sizeNumber(option), b = sizeNumber(pair.options[index]);
        const values = this._optionValues(index, product);
        const gap = !isNaN(a) && !isNaN(b) ? Math.abs(a - b) : Math.abs(values.indexOf(option) - values.indexOf(pair.options[index]));
        return sum + gap * (index < optionIndex ? 1000 : 1);
      }, 0);

      const candidates = product.variants.filter((variant) => variant.options[optionIndex] === value);
      const fallback = candidates.filter((variant) => variant.available).sort((a, b) => distance(a) - distance(b))[0] || candidates[0];

      if (fallback) {
        pair.options = [...fallback.options];
      }
    }

    if (pairIndex === 0) {
      const variant = this._variantFor(pair.options, product);
      const picker = document.querySelector(`variant-picker[form="${this.getAttribute("form")}"]`);

      if (variant && picker?.selectVariant) {
        picker.selectVariant(variant.id); // Triggers "variant:change", which updates the untouched pairs
      }
    } else {
      pair.touched = true;
    }

    this._update();
  }

  /**
   * Switches a pair to another model (keeping the size / gender when the new model offers them).
   */
  _selectModel(pairIndex, productId, colorValue) {
    const pair = this.pairs[pairIndex];
    const product = this.modelsById.get(productId)?.product;

    if (!product) {
      return;
    }

    const previous = this._productOf(pair);
    const previousNames = this._optionNames(previous).map((name) => name.toLowerCase());
    const names = this._optionNames(product);
    const colorIndex = this._colorIndex(product);

    const wanted = names.map((name, index) => {
      if (index === colorIndex && colorValue) return colorValue;
      const previousIndex = previousNames.indexOf(name.toLowerCase());
      return previousIndex === -1 ? null : pair.options[previousIndex];
    });

    const score = (variant) => variant.options.reduce((sum, value, index) => {
      if (wanted[index] === null || wanted[index] === undefined) return sum;
      if (value === wanted[index]) return sum + (index === colorIndex ? 100 : 10);
      const a = sizeNumber(value), b = sizeNumber(wanted[index]);
      return sum - (!isNaN(a) && !isNaN(b) ? Math.abs(a - b) : 5);
    }, variant.available ? 1000 : 0);

    const best = [...product.variants].sort((a, b) => score(b) - score(a))[0];

    pair.productId = product.id;
    pair.options = [...best.options];
    pair.touched = true;

    // The option selects depend on the model, so the row is rebuilt (keeping the scroll position of the model cards)
    const row = this.querySelector(`[data-pair-row="${pairIndex}"]`);

    if (row) {
      const scrollLeft = row.querySelector(".nf-models__track")?.scrollLeft || 0;
      const newRow = this._buildRow(pairIndex);
      row.replaceWith(newRow);
      const track = newRow.querySelector(".nf-models__track");
      if (track) {
        track.style.scrollBehavior = "auto";
        track.scrollLeft = scrollLeft;
        requestAnimationFrame(() => { track.scrollLeft = scrollLeft; track.style.scrollBehavior = ""; });
      }
    }

    this._update();
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Rendering
   * ------------------------------------------------------------------------------------------------------------ */

  _buildRows(container, quantity) {
    for (let pairIndex = 0; pairIndex < quantity; pairIndex++) {
      container.appendChild(this._buildRow(pairIndex));
    }
  }

  _buildRow(pairIndex) {
    const pair = this.pairs[pairIndex];
    const product = this._productOf(pair);
    const withModels = pairIndex > 0 && this.mixModels;

    const row = document.createElement("div");
    row.className = `nf-breaks__pair ${withModels ? "nf-breaks__pair--models" : ""}`;
    row.setAttribute("data-pair-row", pairIndex);

    const label = document.createElement("div");
    label.className = "nf-breaks__pair-label";
    const ordinal = this.labels.ordinals[pairIndex] || `#${pairIndex + 1}`;
    label.textContent = pairIndex === 0 ? this.labels.first : (withModels ? this.labels.model : this.labels.other).replace("[n]", pairIndex + 1).replace("[ordinal]", ordinal);
    row.appendChild(label);

    if (withModels) {
      const arrows = document.createElement("span");
      arrows.className = "nf-models__arrows";
      arrows.innerHTML = `<button type="button" data-models-scroll="-1" aria-label="Previous"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button><button type="button" data-models-scroll="1" aria-label="Next"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></button>`;
      label.appendChild(arrows);

      const track = document.createElement("div");
      track.className = "nf-models__track";
      track.innerHTML = this.models.map((model) => this._modelCardHtml(model)).join("");
      row.appendChild(track);
    }

    const body = document.createElement("div");
    body.className = "nf-breaks__pair-body";

    const thumb = document.createElement("img");
    thumb.className = "nf-breaks__pair-thumb";
    thumb.alt = "";
    thumb.width = 52;
    thumb.height = 52;
    thumb.loading = "lazy";
    body.appendChild(thumb);

    if (withModels) {
      const summary = document.createElement("span");
      summary.className = "nf-breaks__pair-summary";
      summary.setAttribute("data-pair-summary", "");
      body.appendChild(summary);
    }

    const colorIndex = withModels ? this._colorIndex(product) : -1;
    const selects = document.createElement("div");
    selects.className = "nf-breaks__pair-selects";

    this._optionNames(product).forEach((name, optionIndex) => {
      if (optionIndex === colorIndex) return; // Picked with the swatches in the model card

      const wrapper = document.createElement("label");
      wrapper.className = "nf-breaks__select";

      const srOnly = document.createElement("span");
      srOnly.className = "sr-only";
      srOnly.textContent = `${label.textContent} – ${name}`;

      const select = document.createElement("select");
      select.setAttribute("data-pair", pairIndex);
      select.setAttribute("data-option", optionIndex);

      wrapper.append(srOnly, select);
      selects.appendChild(wrapper);
    });

    body.appendChild(selects);
    row.appendChild(body);
    return row;
  }

  _modelCardHtml(model) {
    const product = model.product, meta = model.meta || {};
    const colorIndex = this._colorIndex(product);
    const colors = colorIndex === -1 ? [] : this._optionValues(colorIndex, product);
    const image = this._imageFor(product.variants.find((variant) => variant.available) || product.variants[0], product);

    const swatches = colors.length > 1 ? `<span class="nf-models__swatches">${colors.map((color) => {
      const variant = product.variants.find((item) => item.options[colorIndex] === color);
      const background = this.swatches[color.toLowerCase()] || (variant ? `url(${this._imageFor(variant, product, 80)}) center / cover` : "#ddd");
      return `<span class="nf-models__swatch" data-model-swatch="${this._escape(color)}" title="${this._escape(color)}" style="background: ${background}"></span>`;
    }).join("")}</span>` : "";

    return `<button type="button" class="nf-models__card" data-model-card="${product.id}">
      <span class="nf-models__media">
        ${image ? `<img src="${image}" alt="" loading="lazy" width="200" height="200">` : ""}
        ${meta.badge ? `<span class="nf-models__badge">${this._escape(meta.badge)}</span>` : ""}
        <span class="nf-models__check" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.2 4.2L19 7"/></svg></span>
        ${swatches}
      </span>
      <span class="nf-models__info">
        ${meta.category ? `<span class="nf-models__category">${this._escape(meta.category)}</span>` : ""}
        <span class="nf-models__name">${this._escape(meta.title || product.title)}</span>
        <span class="nf-models__color" data-model-color></span>
      </span>
      ${meta.tagline ? `<span class="nf-models__tagline">${this._escape(meta.tagline)}</span>` : ""}
    </button>`;
  }

  _update() {
    if (!this.product) {
      return;
    }

    // 1. Rows of the selected card
    this.querySelectorAll("[data-pair-row]").forEach((row) => {
      const pairIndex = parseInt(row.getAttribute("data-pair-row"), 10);
      const pair = this.pairs[pairIndex];
      const product = this._productOf(pair);
      const variant = this._variantFor(pair.options, product);

      row.querySelectorAll("select[data-pair]").forEach((select) => {
        const optionIndex = parseInt(select.getAttribute("data-option"), 10);
        const values = this._optionValues(optionIndex, product).filter((value) => product.variants.some((item) => {
          return item.options[optionIndex] === value && item.options.slice(0, optionIndex).every((option, index) => option === pair.options[index]);
        }));

        if (Array.from(select.options).map((option) => option.value).join("|") !== values.join("|")) {
          select.replaceChildren(...values.map((value) => new Option(value, value)));
        }

        select.value = pair.options[optionIndex];

        const isSize = optionIndex === this._sizeIndex(product);
        const gender = NfSizes.genderOf(pair.options) || NfSizes.genderOf(this.pairs[0]?.options) || "men";

        Array.from(select.options).forEach((option) => {
          const candidate = [...pair.options];
          candidate[optionIndex] = option.value;
          const available = this._isAvailable(candidate, product);
          const text = isSize ? NfSizes.full(option.value, gender) : option.value;
          option.textContent = available ? text : `${text} – ${this.labels.soldOut}`;
          option.disabled = !this._variantFor(candidate, product) || (!available && optionIndex === pair.options.length - 1);
        });
      });

      const image = this._imageFor(variant, product, 120);
      const thumb = row.querySelector(".nf-breaks__pair-thumb");
      if (image && thumb && thumb.getAttribute("src") !== image) thumb.src = image;

      // Model cards (pairs 2+)
      const colorIndex = this._colorIndex(product);
      const colorValue = colorIndex === -1 ? "" : pair.options[colorIndex];

      row.querySelectorAll("[data-model-card]").forEach((card) => {
        const selected = parseInt(card.getAttribute("data-model-card"), 10) === product.id;
        card.classList.toggle("is-selected", selected);
        card.setAttribute("aria-pressed", selected ? "true" : "false");

        const cardProduct = this.modelsById.get(parseInt(card.getAttribute("data-model-card"), 10))?.product;
        const cardColorIndex = cardProduct ? this._colorIndex(cardProduct) : -1;
        const cardVariant = selected ? variant : cardProduct?.variants.find((item) => item.available) || cardProduct?.variants[0];
        const colorText = card.querySelector("[data-model-color]");
        if (colorText) colorText.textContent = cardColorIndex === -1 || !cardVariant ? "" : cardVariant.options[cardColorIndex];

        const cardImage = card.querySelector(".nf-models__media img");
        const newImage = cardVariant ? this._imageFor(cardVariant, cardProduct, 400) : null;
        if (cardImage && newImage && cardImage.getAttribute("src") !== newImage) cardImage.src = newImage;

        card.querySelectorAll("[data-model-swatch]").forEach((swatch) => swatch.classList.toggle("is-selected", selected && swatch.getAttribute("data-model-swatch") === colorValue));
      });

      const summary = row.querySelector("[data-pair-summary]");
      if (summary) {
        const meta = this.modelsById.get(product.id)?.meta || {};
        summary.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.3l2.8 2.8L16 9.5"/></svg><span>${this._escape(this.labels.summary.replace("[n]", pairIndex + 1).replace("[model]", meta.title || product.title).replace(" · [color]", colorValue ? " · [color]" : "").replace("[color]", colorValue))}</span>`;
      }

      row.classList.toggle("is-unavailable", !variant || !variant.available);
    });

    // 2. Prices of every card
    const totals = this.cards.map((card) => this._totalsFor(card));

    this.cards.forEach((card, index) => {
      const { quantity, total, compare } = totals[index];
      const perItem = this.getAttribute("price-mode") === "per_item";
      const shownPrice = perItem ? Math.round(total / quantity) : total;
      const shownCompare = perItem ? Math.round(compare / quantity) : compare;

      card.querySelector("[data-price]").textContent = formatMoney(shownPrice);

      const compareElement = card.querySelector("[data-compare]");
      const saveElement = card.querySelector("[data-save]");

      if (compareElement) {
        compareElement.textContent = shownCompare > shownPrice ? formatMoney(shownCompare) : "";
      }

      if (saveElement) {
        saveElement.textContent = shownCompare > shownPrice ? saveElement.getAttribute("data-template").replace("[amount]", formatMoney(shownCompare - shownPrice)) : "";
      }
    });

    // 3. Savings bar and add to cart button of the selected card
    const selected = totals[this.cards.indexOf(this.selectedCard)] || totals[0];
    const savingBar = this.querySelector("[data-saving-bar]");

    if (savingBar) {
      const saving = selected.compare - selected.total;
      savingBar.hidden = selected.quantity < 2 || saving <= 0;
      savingBar.querySelector("[data-saving-bar-text]").innerHTML = savingBar.getAttribute("data-template")
        .replace("[amount]", `<strong>${formatMoney(saving)}</strong>`)
        .replace("[bundle]", this.selectedCard.getAttribute("data-label") || "");
    }

    if (this.hasAttribute("atc-total")) {
      const extra = document.querySelector(`nf-insole-upsell[form="${this.getAttribute("form")}"]`)?.getExtraTotals?.(this.getPairVariants()) || { total: 0, compare: 0 };
      this._updateAddToCart({ total: selected.total + extra.total, compare: selected.compare + extra.compare });
    }
  }

  _updateAddToCart({ total, compare }) {
    const mainVariant = this._mainVariant();

    document.querySelectorAll(`.product-info buy-buttons[form="${this.getAttribute("form")}"] button[type="submit"]`).forEach((button) => {
      const textElement = button.getAttribute("is") === "custom-button" ? button.firstElementChild : button;

      if (!textElement) {
        return;
      }

      let priceElement = textElement.querySelector(".nf-atc-total");

      if (!mainVariant?.available) {
        priceElement?.remove();
        return;
      }

      if (!priceElement) {
        priceElement = document.createElement("span");
        priceElement.className = "nf-atc-total";
        textElement.appendChild(priceElement);
      }

      priceElement.innerHTML = `${compare > total ? `<s>${formatMoney(compare)}</s>` : ""}<span>${formatMoney(total)}</span>`;
    });
  }

  /**
   * The variant of every selected pair (used by the insole upsell to add one matching insole per pair).
   */
  getPairVariants() {
    const fallback = this._mainVariant() || this.product?.variants[0];

    return Array.from({ length: this.quantity }, (_, index) => {
      const pair = this.pairs[index];
      return (this.withPairSelectors && pair ? this._variantFor(pair.options, this._productOf(pair)) : null) || fallback;
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------------------------------------------ */

  _totalsFor(card) {
    const quantity = parseInt(card.getAttribute("data-quantity"), 10);
    const discount = parseFloat(card.getAttribute("data-discount") || "0");
    const fallback = this._mainVariant() || this.product.variants[0];
    let price = 0, compare = 0;

    for (let index = 0; index < quantity; index++) {
      const pair = this.withPairSelectors ? this.pairs[index] : this.pairs[0];
      const variant = (pair ? this._variantFor(pair.options, this._productOf(pair)) : null) || fallback;
      price += variant.price;
      compare += Math.max(variant.compare_at_price || 0, variant.price);
    }

    return { quantity, total: Math.round(price * (1 - discount / 100)), compare };
  }

  _productOf(pair) {
    return this.modelsById.get(pair?.productId)?.product || this.product;
  }

  _mainVariant() {
    const id = parseInt(this.form?.elements.namedItem("id")?.value, 10);
    return this.product?.variants.find((variant) => variant.id === id);
  }

  _variantFor(options, product = this.product) {
    return product.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  _isAvailable(options, product = this.product) {
    return !!this._variantFor(options, product)?.available;
  }

  _optionNames(product = this.product) {
    return (product.options || []).map((option) => typeof option === "string" ? option : option.name);
  }

  _colorIndex(product = this.product) {
    return this._optionNames(product).findIndex((name) => ["color", "colour", "kleur", "farbe", "couleur"].includes(name.toLowerCase()));
  }

  _sizeIndex(product = this.product) {
    const names = this._optionNames(product).map((name) => name.toLowerCase());
    const index = names.findIndex((name) => name.includes("size") || name.includes("maat"));
    return index === -1 ? names.length - 1 : index;
  }

  _optionValues(optionIndex, product = this.product) {
    const values = [...new Set(product.variants.map((variant) => variant.options[optionIndex]))];

    // Sizes: keep them in numeric order even when variants were created per gender (women sizes, then men sizes)
    return values.every((value) => !isNaN(sizeNumber(value))) ? values.sort((a, b) => sizeNumber(a) - sizeNumber(b)) : values;
  }

  _imageFor(variant, product, width = 400) {
    const src = variant?.featured_image?.src || product?.featured_image || (product?.images || [])[0];
    if (!src) return null;
    const url = typeof src === "string" ? src : src.src;
    return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
  }

  _parseSwatches(config) {
    const map = {};
    String(config || "").split(/\n+/).forEach((line) => {
      const [name, value] = line.split(":");
      if (name && value && value.trim().startsWith("#")) map[name.trim().toLowerCase()] = value.trim();
    });
    return map;
  }

  _escape(text) {
    const div = document.createElement("div");
    div.textContent = text ?? "";
    return div.innerHTML;
  }
}

/**
 * Size guide: switches between charts (tabs) and converts the length column between cm and inches.
 */
class NfSizeGuide extends HTMLElement {
  connectedCallback() {
    // Switch to the chart matching the selected variant (e.g. "Women" / "Men")
    this._onVariantChanged = (event) => {
      const values = (event.detail?.variant?.options || []).map((value) => value.toLowerCase().trim());
      const tab = Array.from(this.querySelectorAll("[data-tab]")).find((item) => values.includes(item.textContent.toLowerCase().trim()));
      tab?.click();
    };
    document.addEventListener("variant:change", this._onVariantChanged);

    this.addEventListener("click", (event) => {
      const tab = event.target.closest("[data-tab]");

      if (!tab) {
        return;
      }

      this.querySelectorAll("[data-tab]").forEach((item) => {
        const selected = item === tab;
        item.setAttribute("aria-selected", selected ? "true" : "false");
        const panel = this.querySelector(`#${CSS.escape(item.getAttribute("aria-controls"))}`);
        if (panel) panel.hidden = !selected;
      });
    });

    this.addEventListener("change", (event) => {
      if (!event.target.matches("[data-unit]")) {
        return;
      }

      const inches = event.target.value === "in";

      this.querySelectorAll("[data-cm]").forEach((cell) => {
        const cm = parseFloat(cell.getAttribute("data-cm").replace(",", "."));
        cell.textContent = isNaN(cm) ? cell.getAttribute("data-cm") : inches ? (cm / 2.54).toFixed(1) : cell.getAttribute("data-cm");
      });

      this.querySelectorAll("[data-length-unit]").forEach((unit) => {
        unit.textContent = inches ? "in" : "cm";
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener("variant:change", this._onVariantChanged);
  }
}

/**
 * When a visitor arrives on a product page from a women's or men's collection (and no variant is set in the URL),
 * pre-select the matching gender option.
 */
const preselectGenderFromCollection = async () => {
  const fieldset = document.querySelector("[data-nf-gender-option]");
  if (!fieldset || new URL(window.location.href).searchParams.has("variant")) {
    return;
  }

  let referrerPath = "";
  try { referrerPath = document.referrer ? new URL(document.referrer).pathname : ""; } catch (e) {}
  const path = `${window.location.pathname} ${referrerPath}`.toLowerCase();
  const match = path.match(/\/collections\/[^/\s]*?(women|woman|dames|men|heren)(?=[^a-z]|$)/);

  if (!match) {
    return;
  }

  const gender = ["women", "woman", "dames"].includes(match[1]) ? ["women", "woman", "dames", "vrouwen"] : ["men", "man", "heren", "mannen"];
  const picker = fieldset.closest("variant-picker");
  const position = parseInt(fieldset.getAttribute("data-nf-gender-option"), 10) - 1;

  for (let i = 0; i < 30 && !picker?.product; i++) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const current = picker?.selectedVariant;
  if (!picker?.product || !current || gender.includes(current.options[position].toLowerCase())) {
    return;
  }

  const variants = picker.product.variants.filter((variant) => gender.includes(variant.options[position].toLowerCase()));
  const target = variants.find((variant) => variant.available) || variants[0];

  if (target) {
    picker.selectVariant(target.id);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", preselectGenderFromCollection);
} else {
  preselectGenderFromCollection();
}

/**
 * Product form submission for the bundle picker and the insole upsell.
 *
 * The theme only knows how to add one variant. When several pairs and/or insoles are selected, this takes over the
 * submission and sends every item explicitly to the cart, then triggers the same events as the theme so that the cart
 * drawer opens and updates as usual.
 */
window.NorellaVersion = 23;

/**
 * Product gallery in "grid" layout (desktop): the theme only scrolls to the image of the selected variant, which does
 * nothing in a grid. Here the images of the selected color are moved to the top of the grid instead.
 *
 * The images of a variant are the ones from its own image up to the image of the next variant (the order of the
 * product media in Shopify), so no alt text is needed.
 */
const nfReorderGallery = (form, product, variant) => {
  const gallery = form && document.querySelector(`product-gallery.product-gallery--desktop-grid[form="${form.getAttribute("id")}"]`);
  const list = gallery?.querySelector(".product-gallery__media-list");

  if (!list || !product || !variant?.featured_media) {
    return;
  }

  const start = variant.featured_media.position;
  const starts = [...new Set(product.variants.map((item) => item.featured_media?.position).filter(Boolean))].sort((a, b) => a - b);
  const end = starts.find((position) => position > start) || Infinity;
  const items = Array.from(list.querySelectorAll(":scope > .product-gallery__media"));

  items.forEach((item, index) => {
    const position = index + 1;
    item.classList.toggle("nf-media-first", position >= start && position < end);
    item.classList.toggle("nf-media-lead", position === start);
  });

  list.classList.add("nf-has-lead");
};

document.addEventListener("variant:change", (event) => nfReorderGallery(event.target, event.detail.product, event.detail.variant));

// A variant opened directly from a link (?variant=...) gets the same order on page load
(async () => {
  const variantId = parseInt(new URLSearchParams(window.location.search).get("variant"), 10);

  for (const gallery of document.querySelectorAll("product-gallery.product-gallery--desktop-grid[form]")) {
    const form = document.forms[gallery.getAttribute("form")];
    const handle = document.querySelector(`variant-picker[form="${gallery.getAttribute("form")}"]`)?.getAttribute("handle");

    if (!variantId || !form || !handle) {
      continue;
    }

    try {
      const product = await (await fetch(`${window.Shopify?.routes?.root || "/"}products/${handle}.js`)).json();
      nfReorderGallery(form, product, product.variants.find((item) => item.id === variantId));
    } catch (error) {
      // The default order is kept
    }
  }
})();

const nfProductForms = new WeakSet();

const nfInstallSubmitHandler = (form) => {
  if (!form || nfProductForms.has(form)) {
    return;
  }

  nfProductForms.add(form);

  // Captured on the document so we always run before the theme's own submit handler (which sits on the form itself)
  document.addEventListener("submit", async (event) => {
    if (event.target !== form) {
      return;
    }

    const formId = form.getAttribute("id");
    const bundle = document.querySelector(`nf-quantity-breaks[form="${formId}"]`);
    const upsell = document.querySelector(`nf-insole-upsell[form="${formId}"]`);

    if (upsell && !form._nfConfirmed) {
      const dialog = upsell.popupToShow();

      if (dialog) {
        event.preventDefault();
        event.stopImmediatePropagation();
        upsell.openPopup(dialog);
        return;
      }
    }

    form._nfConfirmed = false;

    const mainId = parseInt(form.elements.namedItem("id")?.value, 10);
    const items = [];
    const push = (id, quantity = 1) => {
      const existing = items.find((item) => item.id === id);
      existing ? existing.quantity += quantity : items.push({ id, quantity });
    };

    if (bundle) {
      bundle.getPairVariants().forEach((variant) => variant && push(variant.id));
    } else {
      push(mainId, parseInt(new FormData(form).get("quantity") || "1", 10) || 1);
    }

    const extras = upsell ? upsell.getItems() : [];
    extras.forEach((item) => push(item.id, item.quantity));

    // A single variant without add-ons: let the theme handle it natively
    if (items.length === 1 && extras.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Line item properties of the form (if any) are attached to the shoes
    const properties = {};
    new FormData(form).forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== "") properties[match[1]] = value;
    });

    const extraIds = extras.map((item) => item.id);
    items.forEach((item) => {
      if (Object.keys(properties).length && !extraIds.includes(item.id)) item.properties = properties;
    });

    await nfAddToCart(form, items, mainId);
  }, { capture: true });
};

const nfAddToCart = async (form, items, mainId) => {
  const root = window.Shopify?.routes?.root || "/";
  const submitButtons = Array.from(form.elements).filter((element) => element.type === "submit");
  const setBusy = (busy) => submitButtons.forEach((button) => {
    button.toggleAttribute("disabled", busy);
    busy ? button.setAttribute("aria-busy", "true") : button.removeAttribute("aria-busy");
  });

  setBusy(true);

  const sections = ["variant-added"];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ items, sections: sections.join(","), sections_url: `${root}variants/${mainId}` })
    });

    const responseJson = await response.json();

    if (!response.ok) {
      form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: responseJson.description || responseJson.message } }));
      return;
    }

    if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
      window.location.href = `${root}cart`;
      return;
    }

    const cart = await (await fetch(`${root}cart.js`)).json();
    cart.sections = responseJson.sections;

    form.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail: { items: responseJson.items || [responseJson], cart } }));
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
  } finally {
    setBusy(false);
  }
};

/**
 * Insole upsell (transition + orthopedic insoles).
 *
 * - One insole is added per pair, matched to the size (and gender) of that pair's shoe. Customers can change the size
 *   of each insole with a dropdown.
 * - When pop-ups are enabled, adding to cart first shows the transition pop-up (if transition insoles are not selected),
 *   or the orthopedic pop-up (if transition insoles are selected but orthopedic insoles are not). Each pop-up is shown
 *   once per page view.
 */
class NfInsoleUpsell extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute("form")];
    this.shoe = JSON.parse(this.querySelector("[data-shoe-product]")?.textContent || "null");
    this.insoles = {};
    this.querySelectorAll("[data-insole-product]").forEach((script) => {
      const insole = JSON.parse(script.textContent);

      if (Array.isArray(insole?.variants) && insole.variants.length) {
        this.insoles[script.getAttribute("data-insole-product")] = insole;
      }
    });

    this.choices = {}; // Manual size choices: { kind: { pairIndex: { id, matched } } }
    this.shownPopups = new Set();
    this._abort = new AbortController();
    const signal = this._abort.signal;

    this.addEventListener("change", (event) => {
      if (event.target.matches("[data-insole-check]")) {
        this._update();
      } else if (event.target.matches("select[data-insole-size]")) {
        const kind = event.target.getAttribute("data-insole-size"), pairIndex = parseInt(event.target.getAttribute("data-pair"), 10);
        const matched = this._matchInsoleVariant(this.insoles[kind], this._pairVariants()[pairIndex]);
        (this.choices[kind] = this.choices[kind] || {})[pairIndex] = { id: parseInt(event.target.value, 10), matched: matched?.id };
        this._update();
      }
    }, { signal });

    this.form?.addEventListener("variant:change", () => setTimeout(() => this._update(), 0), { signal });
    this.bundle?.addEventListener("change", () => setTimeout(() => this._update(), 0), { signal });
    this.bundle?.addEventListener("click", () => setTimeout(() => this._update(), 0), { signal });

    nfInstallSubmitHandler(this.form);

    this.querySelectorAll("dialog[data-popup]").forEach((dialog) => {
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog || event.target.closest("[data-popup-close]")) {
          dialog.close();
        } else if (event.target.closest("[data-popup-accept]")) {
          this._resolvePopup(dialog, true);
        } else if (event.target.closest("[data-popup-decline]")) {
          this._resolvePopup(dialog, false);
        }
      }, { signal });
    });

    this._update();
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  get bundle() {
    return document.querySelector(`nf-quantity-breaks[form="${this.getAttribute("form")}"]`);
  }

  isSelected(kind) {
    return !!this.querySelector(`[data-insole-card="${kind}"] [data-insole-check]:checked`);
  }

  setSelected(kind, selected) {
    const input = this.querySelector(`[data-insole-card="${kind}"] [data-insole-check]`);

    if (input && !input.disabled && input.checked !== selected) {
      input.checked = selected;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  /**
   * The insole variant for one pair: the customer's own choice, or the one matching that pair's shoe.
   */
  variantFor(kind, pairIndex, pairVariants = this._pairVariants()) {
    const insole = this.insoles[kind];
    const matched = this._matchInsoleVariant(insole, pairVariants[pairIndex]);
    const choice = this.choices[kind]?.[pairIndex];

    // A manual choice is kept until the shoe of that pair changes
    if (choice && choice.matched === matched?.id) {
      return insole.variants.find((variant) => variant.id === choice.id) || matched;
    }

    return matched;
  }

  /**
   * Cart items for the selected insoles (one per pair).
   */
  getItems(pairVariants = this._pairVariants()) {
    pairVariants = this._limit(pairVariants);
    const quantities = new Map();

    Object.keys(this.insoles).filter((kind) => this.isSelected(kind)).forEach((kind) => {
      pairVariants.forEach((_, pairIndex) => {
        const variant = this.variantFor(kind, pairIndex, pairVariants);
        if (variant) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
      });
    });

    return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
  }

  /**
   * Extra amount added by the selected insoles (used by the bundle add to cart button).
   */
  getExtraTotals(pairVariants = this._pairVariants()) {
    pairVariants = this._limit(pairVariants);
    let total = 0, compare = 0;

    Object.keys(this.insoles).filter((kind) => this.isSelected(kind)).forEach((kind) => {
      pairVariants.forEach((_, pairIndex) => {
        const variant = this.variantFor(kind, pairIndex, pairVariants);

        if (variant) {
          total += variant.price;
          compare += Math.max(variant.compare_at_price || 0, variant.price);
        }
      });
    });

    return { total, compare };
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Pop-ups
   * ------------------------------------------------------------------------------------------------------------ */

  popupToShow() {
    if (!this.hasAttribute("popups")) {
      return null;
    }

    const hasTransition = !!this.insoles.transition;
    let kind = null;

    if (hasTransition && !this.isSelected("transition")) {
      kind = "transition";
    } else if ((!hasTransition || this.isSelected("transition")) && this.insoles.orthopedic && !this.isSelected("orthopedic")) {
      kind = "orthopedic";
    }

    if (!kind || this.shownPopups.has(kind)) {
      return null;
    }

    return this.querySelector(`dialog[data-popup="${kind}"]`);
  }

  openPopup(dialog) {
    this.shownPopups.add(dialog.getAttribute("data-popup"));
    dialog.showModal();
    dialog.querySelector("[data-popup-accept]")?.focus();
  }

  _resolvePopup(dialog, accepted) {
    if (accepted) {
      this.setSelected(dialog.getAttribute("data-popup"), true);
    }

    dialog.close();

    if (this.hasAttribute("popup-checkout")) {
      this.form.addEventListener("variant:add", () => window.location.href = `${window.Shopify?.routes?.root || "/"}checkout`, { once: true });
    }

    this.form._nfConfirmed = true;
    this.form.requestSubmit();
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Rendering and helpers
   * ------------------------------------------------------------------------------------------------------------ */

  _update() {
    const pairs = this._pairVariants();

    Object.entries(this.insoles).forEach(([kind, insole]) => {
      const selected = this.isSelected(kind);
      const first = this.variantFor(kind, 0, pairs);

      this.querySelectorAll(`[data-insole-card="${kind}"]`).forEach((card) => {
        const price = card.querySelector("[data-insole-price]");
        const compare = card.querySelector("[data-insole-compare]");
        const quantity = card.querySelector("[data-insole-qty]");

        if (first && price) price.textContent = formatMoney(first.price);
        if (first && compare) compare.textContent = first.compare_at_price > first.price ? formatMoney(first.compare_at_price) : "";

        if (quantity) {
          quantity.hidden = pairs.length < 2;
          quantity.textContent = (this.getAttribute("data-pairs-label") || "× [count]").replace("[count]", pairs.length);
        }

        card.classList.toggle("is-selected", selected);
        this._renderSizes(card, kind, insole, pairs, selected);
      });
    });

    // Let the bundle refresh the add to cart total
    this.bundle?._update?.();
  }

  /**
   * Size dropdown(s) of a selected insole: one per pair, pre-selected on the size matching that pair's shoe.
   */
  _renderSizes(card, kind, insole, pairs, selected) {
    const container = card.querySelector("[data-insole-sizes]");

    if (!container) {
      return;
    }

    const show = selected && insole.variants.length > 1;
    container.hidden = !show;

    if (!show) {
      container.innerHTML = "";
      return;
    }

    const yourSize = this.getAttribute("data-your-size-label") || "Your size";
    const pairLabel = this.getAttribute("data-pair-label") || "Pair [n]";
    const soldOut = this.getAttribute("data-sold-out-label") || "Sold out";
    const signature = `${pairs.length}|${pairs.map((variant) => variant?.id).join(",")}`;

    if (container.getAttribute("data-signature") !== signature) {
      container.setAttribute("data-signature", signature);
      container.innerHTML = pairs.map((_, pairIndex) => `
        <label class="nf-insole-card__size">
          <span>${this._escape(container.getAttribute("data-label") || "Size")}${pairs.length > 1 ? ` · ${this._escape(pairLabel.replace("[n]", pairIndex + 1))}` : ""}</span>
          <span class="nf-breaks__select"><select data-insole-size="${kind}" data-pair="${pairIndex}"></select></span>
        </label>`).join("");
    }

    container.querySelectorAll("select[data-insole-size]").forEach((select) => {
      const pairIndex = parseInt(select.getAttribute("data-pair"), 10);
      const matched = this._matchInsoleVariant(insole, pairs[pairIndex]);
      const current = this.variantFor(kind, pairIndex, pairs);

      select.replaceChildren(...insole.variants.map((variant) => {
        const text = `${variant.title}${variant.id === matched?.id ? ` · ${yourSize}` : ""}${variant.available ? "" : ` – ${soldOut}`}`;
        const option = new Option(text, variant.id);
        option.disabled = !variant.available;
        return option;
      }));

      if (current) select.value = current.id;
    });
  }

  /**
   * With the "single" quantity mode only one insole is offered, whatever the number of pairs in the bundle.
   */
  _limit(pairVariants) {
    return this.getAttribute("data-quantity-mode") === "single" ? pairVariants.slice(0, 1) : pairVariants;
  }

  _pairVariants() {
    const bundlePairs = this.bundle?.getPairVariants?.();

    if (bundlePairs?.length) {
      return this._limit(bundlePairs);
    }

    const id = parseInt(this.form?.elements.namedItem("id")?.value, 10);
    const variant = this.shoe?.variants.find((item) => item.id === id) || this.shoe?.variants[0];
    const quantity = parseInt(this.form?.querySelector('[name="quantity"]')?.value || document.querySelector(`[name="quantity"][form="${this.getAttribute("form")}"]`)?.value || "1", 10);

    return this._limit(Array.from({ length: Math.max(1, quantity) }, () => variant));
  }

  /**
   * Finds the insole variant matching a shoe variant: the one sharing the most option values (e.g. "Men" + "US9"),
   * preferring available variants.
   */
  _matchInsoleVariant(insole, shoeVariant) {
    if (!insole?.variants?.length) {
      return null;
    }

    if (insole.variants.length === 1 || !shoeVariant) {
      return insole.variants.find((variant) => variant.available) || insole.variants[0];
    }

    const normalize = (value) => String(value).toLowerCase().replace(/\s+/g, "");
    const shoeValues = shoeVariant.options.map(normalize);

    // Values the shoe itself does not define (e.g. gender for a unisex model) fall back to the first pair's shoe
    const fallbackValues = (this._pairVariants()[0]?.options || []).map(normalize);

    const score = (variant) => variant.options.reduce((sum, value) => {
      const normalized = normalize(value);
      return sum + (shoeValues.includes(normalized) ? 4 : fallbackValues.includes(normalized) ? 1 : 0);
    }, variant.available ? 0.5 : 0);

    return [...insole.variants].sort((a, b) => score(b) - score(a))[0];
  }

  _escape(text) {
    const div = document.createElement("div");
    div.textContent = text ?? "";
    return div.innerHTML;
  }
}

/**
 * Size finder: a 4 step funnel (gender, usual size or foot length, brand, toe room) that recommends a size from the
 * product's own size values, then selects it (and optionally adds it to the cart).
 *
 * Size tables are "US, EU, UK, cm" rows. Adjustments (brand + toe room) are expressed in sizes; one size ≈ 0.8 cm.
 */
class NfSizeFinder extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector("[data-sf-config]").textContent);
    this.product = JSON.parse(this.querySelector("[data-sf-product]")?.textContent || "null");
    this.dialog = this.querySelector("dialog");
    // Visitors from a translated market start in their own size system
    const startSystem = NfSizes.system !== NfSizes.storeSystem ? NfSizes.system : (this.config.defaultSystem || "us");
    this.state = { gender: null, system: startSystem, half: false, value: null, length: null, brand: 0, feel: 0 };
    this.history = [];
    this.tables = NfSizes.tables;
    this.querySelector('[data-sf-system="au"]')?.toggleAttribute("hidden", NfSizes.system !== "au");

    this._abort = new AbortController();
    const signal = this._abort.signal;

    this.addEventListener("click", this._onClick.bind(this), { signal });
    this.addEventListener("change", (event) => {
      if (event.target.matches("[data-sf-half]")) {
        this.state.half = event.target.checked;
        this._renderSizes();
      }
    }, { signal });

    // Any link or button pointing to #size-finder opens the funnel
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest('a[href$="#size-finder"], [data-size-finder-open]');
      if (trigger) {
        event.preventDefault();
        this.open();
      }
    }, { signal });

    this.dialog.addEventListener("click", (event) => event.target === this.dialog && this.dialog.close(), { signal });
    this._renderBrands();
    this._restoreSavedResult();
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  open() {
    this.history = [];
    this._show("gender");
    this.dialog.showModal();
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Navigation
   * ------------------------------------------------------------------------------------------------------------ */

  _onClick(event) {
    const target = event.target.closest("button, [data-sf-open]");
    if (!target) return;

    if (target.matches("[data-sf-open]")) return this.open();
    if (target.matches("[data-sf-close]")) return this.dialog.close();
    if (target.matches("[data-sf-back]")) return this._back();
    if (target.matches("[data-sf-restart]")) return this.open();

    if (target.matches("[data-sf-gender]")) {
      this.state.gender = target.getAttribute("data-sf-gender");
      this.querySelectorAll("[data-sf-gender]").forEach((item) => item.classList.toggle("is-selected", item === target));
      this._renderSizes();
      return this._next("size");
    }

    if (target.matches("[data-sf-system]")) {
      this.state.system = target.getAttribute("data-sf-system");
      return this._renderSizes();
    }

    if (target.matches("[data-sf-size]")) {
      this.state.value = parseFloat(target.getAttribute("data-sf-size"));
      this.state.length = null;
      return this._next("brand");
    }

    if (target.matches("[data-sf-go]")) return this._next(target.getAttribute("data-sf-go"));

    if (target.matches("[data-sf-length-step]")) {
      const input = this.querySelector("[data-sf-length]");
      input.value = Math.min(34, Math.max(18, parseFloat(input.value || "25") + parseFloat(target.getAttribute("data-sf-length-step")))).toFixed(1);
      return;
    }

    if (target.matches("[data-sf-measure-done]")) {
      const length = parseFloat(String(this.querySelector("[data-sf-length]").value).replace(",", "."));
      if (isNaN(length)) return;
      this.state.length = length;
      this.state.value = null;
      return this._calculate();
    }

    if (target.matches("[data-sf-brand]")) {
      this.state.brand = parseFloat(target.getAttribute("data-sf-brand")) || 0;
      return this._next("feel");
    }

    if (target.matches("[data-sf-feel]")) {
      this.state.feel = parseFloat(target.getAttribute("data-sf-feel")) || 0;
      return this._calculate();
    }

    if (target.matches("[data-sf-apply]")) return this._apply();
  }

  _next(panel) {
    this.history.push(this.current);
    this._show(panel);
  }

  _back() {
    const previous = this.history.pop();
    if (previous) this._show(previous);
  }

  _show(panel) {
    this.current = panel;
    this.querySelectorAll("[data-sf-panel]").forEach((item) => item.hidden = item.getAttribute("data-sf-panel") !== panel);

    const steps = { gender: 1, size: 2, measure: 2, brand: 3, feel: 4 };
    const stepLabel = this.querySelector("[data-sf-step-label]");
    stepLabel.textContent = steps[panel] ? this.config.strings.step.replace("[n]", steps[panel]) : "";
    this.querySelector("[data-sf-progress]").style.width = `${steps[panel] ? steps[panel] * 25 - 12.5 : 100}%`;
    this.querySelector("[data-sf-back]").hidden = !this.history.length || panel === "loading";
    this.querySelector(".nf-sf__body").scrollTop = 0;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Rendering
   * ------------------------------------------------------------------------------------------------------------ */

  _renderSizes() {
    const useUsual = NfSizes.usual.length > 0;
    const table = useUsual ? NfSizes.usual : this.tables[this.state.gender || "women"];
    const column = useUsual ? NfSizes.usualColumn(this.state.system, this.state.gender || "women") : NfSizes.column(this.state.system, this.state.gender || "women");
    const values = [...new Set(table.map((row) => row[column]))].filter((value) => this.state.half || Number.isInteger(value)).sort((a, b) => a - b);

    this.querySelectorAll("[data-sf-system]").forEach((tab) => tab.setAttribute("aria-selected", tab.getAttribute("data-sf-system") === this.state.system ? "true" : "false"));
    this.querySelector("[data-sf-sizes]").innerHTML = values.map((value) => `<button type="button" class="nf-sf__size" data-sf-size="${value}">${this._formatNumber(value)}</button>`).join("");

    const gender = this.state.gender === "men" ? this.config.men : this.config.women;
    this.querySelector("[data-sf-showing]").textContent = this.config.strings.showing.replace("[system]", this.state.system.toUpperCase()).replace("[gender]", gender.toLowerCase());
  }

  _renderBrands() {
    const brands = String(this.config.brands || "").split(/\n+/).map((line) => line.trim()).filter(Boolean).map((line) => {
      const [name, offset] = line.split(":");
      return { name: name.trim(), offset: parseFloat(offset) || 0 };
    });

    this.querySelector("[data-sf-brands]").innerHTML = brands.map((brand) => `<button type="button" class="nf-sf__option" data-sf-brand="${brand.offset}">${this._escape(brand.name)}</button>`).join("");
  }

  _calculate() {
    this.history.push(this.current);
    this._show("loading");
    this.querySelectorAll(".nf-sf__checks li").forEach((item, index) => {
      item.classList.remove("is-done");
      setTimeout(() => item.classList.add("is-done"), 450 * (index + 1));
    });

    const result = this._recommend();
    this.result = result;

    setTimeout(() => {
      this._renderResult(result);
      this._show("result");
      this.querySelector("[data-sf-back]").hidden = false;
    }, 1900);
  }

  _renderResult(result) {
    const s = this.config.strings, product = this.config.productTitle;
    const label = this._resultLabel(result);

    this.querySelector("[data-sf-result-size]").textContent = label;
    const primary = NfSizes.system === "au" ? null : NfSizes.system;
    const otherSystems = result.row ? [["US", 0], ["EU", 1], ["UK", 2]].filter(([name]) => name.toLowerCase() !== primary).map(([name, index]) => `${name} ${this._formatNumber(result.row[index])}`) : [];

    this.querySelector("[data-sf-result-pills]").innerHTML = [
      ...otherSystems,
      result.row ? s.fitsUpTo.replace("[length]", result.row[3].toFixed(1)).replace(/^This size fits/i, "Fits") : null
    ].filter(Boolean).map((pill) => `<span>${this._escape(pill)}</span>`).join("");

    const genderName = (this.state.gender === "men" ? this.config.men : this.config.women).toLowerCase();
    const reasons = [
      this.state.length ? s.basedOnLength.replace("[length]", this.state.length.toFixed(1)) : s.basedOnSize.replace("[size]", `${this.state.system.toUpperCase()} ${this._formatNumber(this.state.value)} ${genderName}`),
      this.state.length ? null : (this.state.feel > 0 ? s.feelTight : this.state.feel < 0 ? s.feelLoose : s.feelGood),
      s.trueToSize.replace("[product]", product),
      result.row ? s.fitsUpTo.replace("[length]", result.row[3].toFixed(1)) : null,
      s.checked.replace("[product]", product)
    ].filter(Boolean);

    this.querySelector("[data-sf-reasons]").innerHTML = reasons.map((reason) => `<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.2 4.2L19 7"/></svg>${this._escape(reason)}</li>`).join("");

    const apply = this.querySelector("[data-sf-apply]");
    const variant = this._variantForResult(result);
    const addMode = this.config.ctaMode === "cart" && variant?.available;
    apply.textContent = (addMode ? s.cta : s.ctaSelect).replace("[size]", label);
    apply.disabled = !variant;

    try { localStorage.setItem(`nf-size-${this.getAttribute("data-product-handle")}`, label); } catch (e) {}
    this._restoreSavedResult();
  }

  _restoreSavedResult() {
    let saved = null;
    try { saved = localStorage.getItem(`nf-size-${this.getAttribute("data-product-handle")}`); } catch (e) {}

    const result = this.querySelector("[data-sf-trigger-result]"), label = this.querySelector("[data-sf-trigger-label]");
    if (!saved || !result) return;

    result.hidden = false;
    result.textContent = this.config.strings.yourSize.replace("[size]", saved);
    if (label) label.textContent = this.config.strings.again;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Size logic
   * ------------------------------------------------------------------------------------------------------------ */

  _recommend() {
    const table = this.tables[this.state.gender];
    let targetLength;

    if (this.state.length) {
      targetLength = this.state.length + (this.config.allowance || 0);
    } else {
      if (NfSizes.usual.length > 0) {
        // Like North: translate the usual size to EU with the general brand conversion, apply the adjustments (in sizes),
        // then look that EU size up in the shoe's own chart
        const usualEu = this._lengthFor(NfSizes.usual, NfSizes.usualColumn(this.state.system, this.state.gender), this.state.value, 0);
        targetLength = this._lengthFor(table, 1, usualEu + this.state.brand + this.state.feel);
      } else {
        const column = NfSizes.column(this.state.system, this.state.gender);
        targetLength = this._lengthFor(table, column, this.state.value) + (this.state.brand + this.state.feel) * 0.8;
      }
    }

    // Candidate sizes: the product's own size values for this gender, mapped onto the size table
    const storeSystem = NfSizes.storeSystem;
    const storeColumn = { us: 0, eu: 1, uk: 2 }[storeSystem];
    const candidates = this._productSizeValues().map((value) => {
      const number = parseFloat(String(value).replace(",", ".").replace(/[^0-9.]/g, ""));
      const row = table.find((item) => item[storeColumn] === number);
      return row ? { value, row } : null;
    }).filter(Boolean).sort((a, b) => a.row[3] - b.row[3]);

    // Without product sizes (e.g. on a page), recommend straight from the table
    const pool = candidates.length ? candidates : table.filter((row) => Number.isInteger(row[storeColumn])).map((row) => ({ value: `${storeSystem.toUpperCase()} ${this._formatNumber(row[storeColumn])}`, row }));
    const match = pool.find((item) => item.row[3] >= targetLength - 0.15) || pool[pool.length - 1];

    return match || { value: null, row: null };
  }

  /**
   * Looks a value up in one column of a size table and returns another column (foot length by default), interpolating
   * between rows for sizes that are not listed (e.g. half sizes).
   */
  _lengthFor(table, column, value, resultColumn = 3) {
    const exact = table.find((row) => row[column] === value);
    if (exact) return exact[resultColumn];

    const sorted = [...table].sort((a, b) => a[column] - b[column]);
    const upper = sorted.find((row) => row[column] > value) || sorted[sorted.length - 1];
    const lower = [...sorted].reverse().find((row) => row[column] < value) || sorted[0];
    if (upper === lower || upper[column] === lower[column]) return upper[resultColumn];

    return lower[resultColumn] + (upper[resultColumn] - lower[resultColumn]) * (value - lower[column]) / (upper[column] - lower[column]);
  }

  _optionIndexes() {
    const names = (this.product?.options || []).map((option) => String(typeof option === "string" ? option : option.name).toLowerCase());
    const genders = [this.config.women, this.config.men].map((value) => value.toLowerCase());

    let gender = names.findIndex((name) => ["gender", "sex", "fit", "geslacht"].includes(name));
    if (gender === -1) gender = names.findIndex((_, index) => this.product.variants.some((variant) => genders.includes(String(variant.options[index]).toLowerCase())));

    let size = names.findIndex((name) => name.includes("size") || name.includes("maat"));
    if (size === -1) size = names.length - 1;

    return { gender, size };
  }

  _productSizeValues() {
    if (!this.product) return [];
    const { gender, size } = this._optionIndexes();
    const genderValue = (this.state.gender === "men" ? this.config.men : this.config.women).toLowerCase();

    return [...new Set(this.product.variants
      .filter((variant) => gender === -1 || String(variant.options[gender]).toLowerCase() === genderValue)
      .map((variant) => variant.options[size]))];
  }

  _variantForResult(result) {
    if (!this.product || !result.value) return null;

    const { gender, size } = this._optionIndexes();
    const genderValue = (this.state.gender === "men" ? this.config.men : this.config.women).toLowerCase();
    const form = document.querySelector('form[action*="/cart/add"]');
    const currentId = parseInt(form?.elements.namedItem("id")?.value, 10);
    const current = this.product.variants.find((variant) => variant.id === currentId);

    const matches = this.product.variants.filter((variant) => {
      return variant.options[size] === result.value && (gender === -1 || String(variant.options[gender]).toLowerCase() === genderValue);
    });

    // Keep the other options (e.g. color) of the current selection when possible
    return matches.find((variant) => current && variant.options.every((value, index) => index === size || index === gender || value === current.options[index]))
      || matches.find((variant) => variant.available) || matches[0] || null;
  }

  _resultLabel(result) {
    if (!result.value) return "—";
    const gender = (this.state.gender === "men" ? this.config.men : this.config.women).toLowerCase();
    const local = NfSizes.convert(result.value, this.state.gender)?.local;
    const value = local || String(result.value).replace(/^([A-Za-z]+)\s*/, "$1 ");
    return `${value} ${gender}`;
  }

  _apply() {
    const variant = this._variantForResult(this.result);
    if (!variant) return;

    const picker = document.querySelector("variant-picker");
    const form = picker ? document.forms[picker.getAttribute("form")] : document.querySelector('form[action*="/cart/add"]');

    picker?.selectVariant?.(variant.id);
    this.dialog.close();

    if (this.config.ctaMode === "cart" && variant.available && form) {
      setTimeout(() => form.requestSubmit(), 150);
    } else {
      picker?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------------------------------------------ */

  _parseTable(text) {
    return String(text || "").split(/\n+/).map((line) => line.split(",").map((cell) => parseFloat(cell.trim().replace(/[^0-9.]/g, "")))).filter((row) => row.length >= 4 && row.every((cell) => !isNaN(cell)));
  }

  _formatNumber(value) {
    return Number.isInteger(value) ? String(value) : String(value).replace(".5", "½");
  }

  _escape(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * Rating summary: a stars row that opens a small popover with rating bars and a few highlighted reviews. "View all"
 * scrolls to the full reviews on the page.
 */
class NfRatingSummary extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector(".nf-rating__trigger");
    this.panel = this.querySelector(".nf-rating__popover");
    this.track = this.querySelector("[data-rating-track]");
    this.dots = this.querySelector("[data-rating-dots]");
    this._abort = new AbortController();
    const signal = this._abort.signal;

    const count = this.track ? this.track.children.length : 0;
    if (this.dots) this.dots.innerHTML = Array.from({ length: count }, (_, index) => `<i class="${index === 0 ? "is-active" : ""}"></i>`).join("");
    this.querySelector(".nf-rating__nav")?.toggleAttribute("hidden", count < 2);

    this.addEventListener("click", (event) => {
      if (event.target.closest(".nf-rating__trigger")) return this.toggle();
      if (event.target.closest("[data-rating-close]")) return this.toggle(false);
      if (event.target.closest("[data-rating-view-all]")) return this._viewAll();

      const arrow = event.target.closest("[data-rating-scroll]");
      if (arrow) this.track.scrollBy({ left: parseInt(arrow.getAttribute("data-rating-scroll"), 10) * this.track.clientWidth, behavior: "smooth" });
    }, { signal });

    this.track?.addEventListener("scroll", () => {
      const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      Array.from(this.dots?.children || []).forEach((dot, dotIndex) => dot.classList.toggle("is-active", dotIndex === index));
    }, { signal, passive: true });

    document.addEventListener("click", (event) => !this.contains(event.target) && this.toggle(false), { signal });
    document.addEventListener("keydown", (event) => event.key === "Escape" && this.toggle(false), { signal });
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  toggle(open = this.panel.hidden) {
    this.panel.hidden = !open;
    this.trigger.setAttribute("aria-expanded", open ? "true" : "false");
    this.classList.toggle("is-open", open);
  }

  _viewAll() {
    this.toggle(false);

    let target = null;
    try { target = document.querySelector(this.getAttribute("target")); } catch (e) {}

    if (target) {
      const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sticky-area-height"), 10) || 0;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset - 16, behavior: "smooth" });
    }
  }
}

/**
 * Cart drawer extras (North style): quantity steppers, upsell cards with size selection, the free e-book and the
 * shipping protection switch. The drawer is re-rendered by the theme, so everything works through event delegation.
 */
const NfCart = (() => {
  const root = () => window.Shopify?.routes?.root || "/";
  const drawer = () => document.getElementById("cart-drawer");
  const post = (path, body) => fetch(`${root()}${path}`, { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify(body) });
  const refresh = () => document.dispatchEvent(new CustomEvent("cart:refresh"));
  let busy = false, autoAdded = false;

  const run = async (element, task) => {
    if (busy) return;
    busy = true;
    element?.classList.add("is-loading");

    try {
      await task();
      refresh();
      document.documentElement.dispatchEvent(new CustomEvent("nf:cart-updated", { bubbles: true }));
    } catch (error) {
      console.error(error);
    } finally {
      busy = false;
      element?.classList.remove("is-loading");
    }
  };

  document.addEventListener("click", (event) => {
    if (!event.target.closest || !drawer()?.contains(event.target)) return;

    // Quantity stepper: change the input and let the theme's line item logic do the rest
    const step = event.target.closest("[data-nf-qty-step]");
    if (step) {
      const input = step.parentElement.querySelector("input[data-line-key]");
      const value = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute("data-nf-qty-step"), 10));
      const max = parseInt(input.getAttribute("max"), 10);
      input.value = !isNaN(max) ? Math.min(max, value) : value;
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    // Upsell carousel arrows
    const arrow = event.target.closest("[data-nf-upsell-scroll]");
    if (arrow) {
      const track = arrow.closest(".nf-cs").querySelector("[data-nf-upsell-track]");
      track.scrollBy({ left: parseInt(arrow.getAttribute("data-nf-upsell-scroll"), 10) * track.clientWidth, behavior: "smooth" });
      return;
    }

    // Upsell "add to order" (adds the free e-book too when one is configured and not in the cart yet)
    const add = event.target.closest("[data-nf-upsell-add]");
    if (add) {
      const box = add.closest(".nf-cs");
      const select = add.closest(".nf-cs__card").querySelector("[data-nf-upsell-select]");
      const items = [{ id: parseInt(select?.value || add.getAttribute("data-nf-upsell-add"), 10), quantity: 1 }];

      run(add, async () => {
        const ebook = parseInt(box.getAttribute("data-ebook-variant"), 10);

        if (ebook) {
          const cart = await (await fetch(`${root()}cart.js`)).json();
          if (!cart.items.some((item) => item.variant_id === ebook)) items.push({ id: ebook, quantity: 1 });
        }

        const response = await post("cart/add.js", { items });
        if (!response.ok) throw new Error((await response.json()).description);
      });
    }
  });

  document.addEventListener("change", (event) => {
    if (!event.target.closest || !drawer()?.contains(event.target)) return;

    const protection = event.target.closest("[data-nf-protection]");
    if (protection && event.target.matches(".nf-protect__input")) {
      const id = parseInt(protection.getAttribute("data-variant-id"), 10);
      const checked = event.target.checked;

      try { sessionStorage.setItem("nf-protection-choice", checked ? "in" : "out"); } catch (e) {}

      run(protection, () => checked ? post("cart/add.js", { items: [{ id, quantity: 1 }] }) : post("cart/update.js", { updates: { [id]: 0 } }));
    }
  });

  // After every render: keep the protection consistent and translate sizes
  const afterRender = () => {
    const element = drawer();
    if (!element) return;

    const protection = element.querySelector("[data-nf-protection]");

    if (protection && !busy) {
      const id = parseInt(protection.getAttribute("data-variant-id"), 10);
      const inCart = protection.getAttribute("data-in-cart") === "true";
      let choice = null;
      try { choice = sessionStorage.getItem("nf-protection-choice"); } catch (e) {}

      if (inCart && protection.getAttribute("data-only-item") === "true") {
        // Protection without any product left: remove it
        run(protection, () => post("cart/update.js", { updates: { [id]: 0 } }));
      } else if (!inCart && protection.hasAttribute("data-preselect") && choice !== "out" && !autoAdded) {
        autoAdded = true; // Only try once per page view
        run(protection, () => post("cart/add.js", { items: [{ id, quantity: 1 }] }));
      }
    }

    // Local size labels (UK / AU / EU) in the line items
    if (NfSizes.system !== NfSizes.storeSystem) {
      element.querySelectorAll("[data-nf-line-options]").forEach((line) => {
        const spans = Array.from(line.querySelectorAll("[data-option-value]"));
        const gender = NfSizes.genderOf(spans.map((span) => span.getAttribute("data-option-value"))) || "men";
        spans.filter((span) => /size|maat/.test(span.getAttribute("data-option-name"))).forEach((span) => {
          span.textContent = NfSizes.full(span.getAttribute("data-option-value"), gender);
        });
      });
    }
  };

  // The theme replaces the drawer content after cart changes, so watch it
  const observe = () => {
    const element = drawer();
    if (!element) return;
    let timer;
    new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(afterRender, 150); }).observe(element, { childList: true, subtree: true });
    afterRender();
  };

  // The count bubble inside the drawer should not count the shipping protection
  document.addEventListener("cart:change", (event) => {
    const protection = drawer()?.querySelector("[data-nf-protection]");
    const bubble = drawer()?.querySelector("[data-nf-cart-count]");
    const id = parseInt(protection?.getAttribute("data-variant-id"), 10);
    const items = event.detail?.cart?.items;

    if (bubble && items) {
      setTimeout(() => { bubble.textContent = items.filter((item) => item.variant_id !== id).reduce((sum, item) => sum + item.quantity, 0); }, 0);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observe);
  } else {
    observe();
  }

  return { refresh };
})();

/**
 * Reveals hidden items (accordion items, reviews...) when the "show more" button is clicked.
 */
class NfShowMore extends HTMLElement {
  connectedCallback() {
    this.querySelector("[data-show-more]")?.addEventListener("click", (event) => {
      this.querySelectorAll("[data-more-item][hidden]").forEach((item) => item.hidden = false);
      event.currentTarget.hidden = true;
    });
  }
}

/**
 * Animates numbers from 0 to their value when they become visible. The final text is always the original one, so
 * values such as "50,000" keep their formatting.
 */
class NfCounter extends HTMLElement {
  connectedCallback() {
    const finalText = this.textContent.trim();
    const target = parseFloat(finalText.replace(/[^0-9.]/g, ""));

    if (isNaN(target) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const decimals = (finalText.split(".")[1] || "").replace(/[^0-9]/g, "").length;
    this.textContent = (0).toFixed(decimals);

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) {
        return;
      }

      observer.disconnect();
      const start = Date.now(), duration = 1400;

      const step = () => {
        const progress = Math.min(1, (Date.now() - start) / duration), eased = 1 - Math.pow(1 - progress, 3);

        if (progress < 1) {
          this.textContent = Math.round(target * eased * Math.pow(10, decimals)) / Math.pow(10, decimals) + "";
          setTimeout(step, 30);
        } else {
          this.textContent = finalText;
        }
      };

      step();
    });

    observer.observe(this);
  }
}

if (!window.customElements.get("nf-countdown")) {
  window.customElements.define("nf-countdown", NfCountdown);
}

if (!window.customElements.get("nf-quantity-breaks")) {
  window.customElements.define("nf-quantity-breaks", NfQuantityBreaks);
}

if (!window.customElements.get("nf-show-more")) {
  window.customElements.define("nf-show-more", NfShowMore);
}

if (!window.customElements.get("nf-counter")) {
  window.customElements.define("nf-counter", NfCounter);
}

if (!window.customElements.get("nf-size-guide")) {
  window.customElements.define("nf-size-guide", NfSizeGuide);
}

if (!window.customElements.get("nf-insole-upsell")) {
  window.customElements.define("nf-insole-upsell", NfInsoleUpsell);
}

if (!window.customElements.get("nf-size-finder")) {
  window.customElements.define("nf-size-finder", NfSizeFinder);
}

if (!window.customElements.get("nf-rating-summary")) {
  window.customElements.define("nf-rating-summary", NfRatingSummary);
}
